import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Building2, Check, ChevronRight, CreditCard, Database,
  ExternalLink, Globe2, Image as ImageIcon, LogOut, Mail, Palette, Plus,
  Save, Search, Settings2, ShieldCheck, Store, Trash2, Users, Clock
} from 'lucide-react';
import './SuperAdminLogin.css';
import './SuperAdminConsole.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DEFAULT_HOURS = DAYS.reduce((hours, day) => ({
  ...hours,
  [day]: { open: day === 'sunday' ? '12:00' : '11:30', close: day === 'friday' || day === 'saturday' ? '23:00' : '22:30', closed: false }
}), {});

const emptyReset = { admin: null, newPassword: '', confirmPassword: '', saving: false, error: '', success: '' };
const emptyNewAdmin = { name: '', email: '', password: '', confirmPassword: '', saving: false, error: '', success: '' };
const emptyTenant = {
  domain: '', name: '', db_name: '', admin_email: '', admin_password: '', admin_name: 'Admin'
};
const defaultSettings = {
  tenantName: '', name: '', description: '', phone: '', instagram: '', address: '', postcode: '',
  deliveryTime: '', pickupTime: '', deliveryFee: '0', minOrder: '0', serviceCharge: '0',
  scheduleInterval: '30', pauseIncomingOrders: false, modulePOS: false, publicDomain: '',
  openingHours: DEFAULT_HOURS, specialHours: [],
  primaryColor: '#f59e0b', secondaryColor: '#d97706', logoUrl: '', defaultItemImage: '',
  banner: '', bannerText: '', seoTitle: '', metaDescription: '', ogTitle: '', ogImage: '', faviconUrl: '',
  stripePublishableKey: '', stripeSecretKey: '', stripeWebhookSecret: '',
  stripeSecretKeyConfigured: false, stripeWebhookSecretConfigured: false,
  smtpEnabled: false, smtpHost: '', smtpPort: '465', smtpUser: '', smtpPassword: '',
  smtpFromEmail: '', smtpUseSsl: true, smtpPasswordConfigured: false
};

const tabs = [
  { id: 'general', label: 'General', icon: Store },
  { id: 'ordering', label: 'Ordering', icon: Settings2 },
  { id: 'hours', label: 'Opening hours', icon: Clock },
  { id: 'appearance', label: 'Brand & content', icon: Palette },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'access', label: 'Admin access', icon: Users }
];

function Field({ label, hint, children, wide = false }) {
  return (
    <label className={`super-console-field${wide ? ' is-wide' : ''}`}>
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function Toggle({ checked, onChange, label, description, disabled = false }) {
  return (
    <div className="super-console-toggle-row">
      <div><strong>{label}</strong>{description && <p>{description}</p>}</div>
      <button
        type="button"
        className={`super-console-switch${checked ? ' is-on' : ''}`}
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
      ><span /></button>
    </div>
  );
}

function ConsoleShell({ children, onHome, onLogout }) {
  return (
    <div className="super-console">
      <aside className="super-console-sidebar">
        <button className="super-console-brand" onClick={onHome}><img src="/Orderkit_BLK.svg" alt="Orderkit" /></button>
        <nav><button className="is-active" onClick={onHome}><Building2 />Restaurants</button></nav>
        <div className="super-console-sidebar-foot">
          <span><ShieldCheck />Secure console</span>
          <button onClick={onLogout}><LogOut />Log out</button>
        </div>
      </aside>
      <main className="super-console-main">{children}</main>
    </div>
  );
}

function tenantFormFromPayload(payload) {
  const settings = payload?.settings || {};
  return {
    ...defaultSettings,
    tenantName: payload?.tenant?.name || settings.name || '',
    name: settings.name || '',
    description: settings.description || '',
    phone: settings.phone || '',
    instagram: settings.instagram || '',
    address: settings.address || '',
    postcode: settings.postcode || '',
    deliveryTime: settings.deliveryTime || '',
    pickupTime: settings.pickupTime || '',
    deliveryFee: String(settings.deliveryFee ?? 0),
    minOrder: String(settings.minOrder ?? 0),
    serviceCharge: String(settings.serviceCharge ?? 0),
    scheduleInterval: String(settings.scheduleInterval ?? 30),
    publicDomain: settings.domain || payload?.tenant?.domain || '',
    openingHours: { ...DEFAULT_HOURS, ...(settings.openingHours || {}) },
    specialHours: Array.isArray(settings.specialHours) ? settings.specialHours : [],
    primaryColor: settings.primaryColor || '#f59e0b',
    secondaryColor: settings.secondaryColor || '#d97706',
    logoUrl: settings.logoUrl || '',
    defaultItemImage: settings.defaultItemImage || '',
    banner: settings.banner || '',
    bannerText: settings.bannerText || '',
    seoTitle: settings.seoTitle || '',
    metaDescription: settings.metaDescription || '',
    ogTitle: settings.ogTitle || '',
    ogImage: settings.ogImage || '',
    faviconUrl: settings.faviconUrl || '',
    smtpPort: String(settings.smtpPort ?? 465),
    pauseIncomingOrders: Boolean(settings.pauseIncomingOrders),
    modulePOS: Boolean(settings.modulePOS),
    stripePublishableKey: settings.stripePublishableKey || '',
    stripeSecretKeyConfigured: Boolean(settings.stripeSecretKeyConfigured),
    stripeWebhookSecretConfigured: Boolean(settings.stripeWebhookSecretConfigured),
    smtpEnabled: Boolean(settings.smtpEnabled),
    smtpHost: settings.smtpHost || '',
    smtpUser: settings.smtpUser || '',
    smtpFromEmail: settings.smtpFromEmail || '',
    smtpUseSsl: settings.smtpUseSsl !== false,
    smtpPasswordConfigured: Boolean(settings.smtpPasswordConfigured),
    stripeSecretKey: '', stripeWebhookSecret: '', smtpPassword: ''
  };
}

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const { domain: routeDomain } = useParams();
  const [apiKey, setApiKey] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pageError, setPageError] = useState('');
  const [search, setSearch] = useState('');
  const [adminManager, setAdminManager] = useState(null);
  const [tenantForm, setTenantForm] = useState(defaultSettings);
  const [tenantTab, setTenantTab] = useState('general');
  const [tenantSaving, setTenantSaving] = useState(false);
  const [operationalToggleSaving, setOperationalToggleSaving] = useState('');
  const today = new Date().toLocaleDateString('en-CA');
  const [specialDraft, setSpecialDraft] = useState({ startDate: today, endDate: today, closed: true, open: '16:00', close: '23:00', label: '' });
  const [tenantMessage, setTenantMessage] = useState('');
  const [passwordReset, setPasswordReset] = useState(emptyReset);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState(emptyNewAdmin);
  const [newTenant, setNewTenant] = useState(emptyTenant);

  const tokenHeaders = (json = false) => ({
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${localStorage.getItem('super_admin_token')}`
  });

  const readJson = async (response) => {
    if (response.status === 401) {
      handleUnauthorised();
      throw new Error('Your session has expired. Please sign in again.');
    }
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'The request could not be completed');
    return data;
  };

  useEffect(() => {
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'robots');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', 'noindex, nofollow');
    return () => tag.setAttribute('content', 'index, follow');
  }, []);

  useEffect(() => {
    if (localStorage.getItem('super_admin_token')) {
      setIsAuthenticated(true);
      loadTenants(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!routeDomain) {
      if (adminManager) setAdminManager(null);
      return;
    }
    if (adminManager) return;
    const tenant = tenants.find(item => item.domain === decodeURIComponent(routeDomain));
    if (tenant) {
      openTenantWorkspace(tenant, false);
    } else if (tenants.length) {
      setPageError('That tenant could not be found.');
      navigate('/');
    }
  }, [isAuthenticated, routeDomain, tenants, adminManager]);

  const handleUnauthorised = () => {
    localStorage.removeItem('super_admin_token');
    setIsAuthenticated(false);
    setAdminManager(null);
  };

  const loadTenants = async (useStoredToken = false) => {
    try {
      setLoading(true);
      setPageError('');
      let token = localStorage.getItem('super_admin_token');
      if (!token || !useStoredToken) {
        const cleanApiKey = apiKey.trim().replace(/[^\x20-\x7E]/g, '');
        const response = await fetch(`${API}/api/super-admin/login`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apiKey: cleanApiKey, totpCode: totpCode.trim() })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Login failed');
        token = data.token;
        localStorage.setItem('super_admin_token', token);
      }
      const data = await readJson(await fetch(`${API}/api/super-admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      }));
      setTenants(data);
      setIsAuthenticated(true);
    } catch (error) {
      setPageError(error.message);
      if (!localStorage.getItem('super_admin_token')) setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      setPageError('');
      const data = await readJson(await fetch(`${API}/api/super-admin/tenants`, {
        method: 'POST', headers: tokenHeaders(true), body: JSON.stringify(newTenant)
      }));
      setNewTenant(emptyTenant);
      setShowCreateForm(false);
      await loadTenants(true);
      const created = { ...data, active: true };
      await openTenantWorkspace(created);
    } catch (error) {
      setPageError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteTenant = async (tenant) => {
    if (!window.confirm(`Deactivate ${tenant.name}? Its website and data will remain, but tenant access will be disabled.`)) return;
    try {
      setLoading(true);
      await readJson(await fetch(`${API}/api/super-admin/tenants/${encodeURIComponent(tenant.domain)}`, {
        method: 'DELETE', headers: tokenHeaders()
      }));
      await loadTenants(true);
    } catch (error) {
      setPageError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openTenantWorkspace = async (tenant, updateRoute = true) => {
    if (updateRoute) navigate(`/tenants/${encodeURIComponent(tenant.domain)}`);
    setAdminManager({ tenant, admins: [], loading: true, error: '' });
    setTenantTab('general');
    setTenantMessage('');
    setPasswordReset(emptyReset);
    setNewAdmin(emptyNewAdmin);
    setShowCreateAdmin(false);
    try {
      const [settings, access] = await Promise.all([
        readJson(await fetch(`${API}/api/super-admin/tenants/${encodeURIComponent(tenant.domain)}/settings`, { headers: tokenHeaders() })),
        readJson(await fetch(`${API}/api/super-admin/tenants/${encodeURIComponent(tenant.domain)}/admins`, { headers: tokenHeaders() }))
      ]);
      setTenantForm(tenantFormFromPayload(settings));
      setAdminManager({ tenant: settings.tenant, admins: access.admins || [], loading: false, error: '' });
    } catch (error) {
      setAdminManager(current => ({ ...current, loading: false, error: error.message }));
    }
  };

  const closeTenantWorkspace = () => {
    setAdminManager(null);
    setTenantMessage('');
    navigate('/');
  };

  const updateField = (field, value) => setTenantForm(current => ({ ...current, [field]: value }));

  const updateHours = (day, field, value) => setTenantForm(current => ({
    ...current,
    openingHours: {
      ...current.openingHours,
      [day]: { ...(current.openingHours[day] || DEFAULT_HOURS[day]), [field]: value }
    }
  }));

  const addSpecialHours = () => {
    if (!specialDraft.startDate || !specialDraft.endDate || specialDraft.endDate < specialDraft.startDate) {
      setTenantMessage('Enter a valid special-hours date range.');
      return;
    }
    if (!specialDraft.closed && (!specialDraft.open || !specialDraft.close)) {
      setTenantMessage('Enter both opening and closing times.');
      return;
    }
    const overlaps = tenantForm.specialHours.some(entry =>
      specialDraft.startDate <= entry.endDate && specialDraft.endDate >= entry.startDate
    );
    if (overlaps) {
      setTenantMessage('These dates overlap an existing special-hours entry.');
      return;
    }
    const entry = {
      id: `special_${Date.now()}`,
      startDate: specialDraft.startDate,
      endDate: specialDraft.endDate,
      closed: specialDraft.closed,
      label: specialDraft.label.trim(),
      ...(!specialDraft.closed ? { open: specialDraft.open, close: specialDraft.close } : {})
    };
    updateField('specialHours', [...tenantForm.specialHours, entry]);
    setTenantMessage('Special hours added. Use Save changes to publish them.');
  };

  const removeSpecialHours = id => {
    updateField('specialHours', tenantForm.specialHours.filter(entry => entry.id !== id));
    setTenantMessage('Special hours removed. Use Save changes to publish the change.');
  };

  const saveOperationalToggle = async (field, enabled) => {
    if (!adminManager?.tenant?.domain || operationalToggleSaving) return;
    const route = field === 'modulePOS' ? 'modules/pos' : 'pause-orders';
    try {
      setOperationalToggleSaving(field);
      setTenantMessage('');
      const result = await readJson(await fetch(
        `${API}/api/super-admin/tenants/${encodeURIComponent(adminManager.tenant.domain)}/${route}`,
        { method: 'PUT', headers: tokenHeaders(true), body: JSON.stringify({ enabled }) }
      ));
      const savedValue = field === 'modulePOS'
        ? Boolean(result.modulePOS)
        : Boolean(result.pauseIncomingOrders);
      updateField(field, savedValue);
      setTenantMessage('Changes saved');
      window.setTimeout(() => setTenantMessage(''), 3000);
    } catch (error) {
      setTenantMessage(error.message);
    } finally {
      setOperationalToggleSaving('');
    }
  };

  const saveTenantSettings = async () => {
    try {
      setTenantSaving(true);
      setTenantMessage('');
      const payload = {
        tenantName: tenantForm.tenantName.trim(), name: tenantForm.name.trim(),
        description: tenantForm.description, phone: tenantForm.phone, instagram: tenantForm.instagram,
        address: tenantForm.address, postcode: tenantForm.postcode,
        deliveryTime: tenantForm.deliveryTime, pickupTime: tenantForm.pickupTime,
        deliveryFee: Number(tenantForm.deliveryFee || 0), minOrder: Number(tenantForm.minOrder || 0),
        serviceCharge: Number(tenantForm.serviceCharge || 0),
        scheduleInterval: Number(tenantForm.scheduleInterval || 30),
        pauseIncomingOrders: tenantForm.pauseIncomingOrders, modulePOS: tenantForm.modulePOS,
        domain: tenantForm.publicDomain.trim(),
        openingHours: tenantForm.openingHours, specialHours: tenantForm.specialHours,
        primaryColor: tenantForm.primaryColor, secondaryColor: tenantForm.secondaryColor,
        logoUrl: tenantForm.logoUrl, defaultItemImage: tenantForm.defaultItemImage,
        banner: tenantForm.banner, bannerText: tenantForm.bannerText,
        seoTitle: tenantForm.seoTitle, metaDescription: tenantForm.metaDescription,
        ogTitle: tenantForm.ogTitle, ogImage: tenantForm.ogImage, faviconUrl: tenantForm.faviconUrl,
        stripePublishableKey: tenantForm.stripePublishableKey,
        stripeSecretKey: tenantForm.stripeSecretKey,
        stripeWebhookSecret: tenantForm.stripeWebhookSecret,
        smtpEnabled: tenantForm.smtpEnabled, smtpHost: tenantForm.smtpHost,
        smtpPort: Number(tenantForm.smtpPort || 465), smtpUser: tenantForm.smtpUser,
        smtpPassword: tenantForm.smtpPassword, smtpFromEmail: tenantForm.smtpFromEmail,
        smtpUseSsl: tenantForm.smtpUseSsl
      };
      const result = await readJson(await fetch(
        `${API}/api/super-admin/tenants/${encodeURIComponent(adminManager.tenant.domain)}/settings`,
        { method: 'PUT', headers: tokenHeaders(true), body: JSON.stringify(payload) }
      ));
      setTenantForm(tenantFormFromPayload(result));
      setAdminManager(current => ({ ...current, tenant: result.tenant }));
      setTenants(current => current.map(item => item.domain === result.tenant.domain ? { ...item, name: result.tenant.name } : item));
      setTenantMessage('Changes saved');
      window.setTimeout(() => setTenantMessage(''), 3000);
    } catch (error) {
      setTenantMessage(error.message);
    } finally {
      setTenantSaving(false);
    }
  };

  const createTenantAdmin = async (event) => {
    event.preventDefault();
    if (newAdmin.password.length < 10) {
      setNewAdmin(current => ({ ...current, error: 'Use at least 10 characters.', success: '' })); return;
    }
    if (newAdmin.password !== newAdmin.confirmPassword) {
      setNewAdmin(current => ({ ...current, error: 'The passwords do not match.', success: '' })); return;
    }
    setNewAdmin(current => ({ ...current, saving: true, error: '', success: '' }));
    try {
      const data = await readJson(await fetch(`${API}/api/super-admin/tenants/${encodeURIComponent(adminManager.tenant.domain)}/admins`, {
        method: 'POST', headers: tokenHeaders(true),
        body: JSON.stringify({ name: newAdmin.name, email: newAdmin.email, password: newAdmin.password })
      }));
      setAdminManager(current => ({ ...current, admins: [...current.admins, data.admin] }));
      setNewAdmin({ ...emptyNewAdmin, success: `Admin login created for ${data.admin.email}.` });
    } catch (error) {
      setNewAdmin(current => ({ ...current, saving: false, error: error.message, success: '' }));
    }
  };

  const resetAdminPassword = async (event) => {
    event.preventDefault();
    if (passwordReset.newPassword.length < 10) {
      setPasswordReset(current => ({ ...current, error: 'Use at least 10 characters.', success: '' })); return;
    }
    if (passwordReset.newPassword !== passwordReset.confirmPassword) {
      setPasswordReset(current => ({ ...current, error: 'The passwords do not match.', success: '' })); return;
    }
    setPasswordReset(current => ({ ...current, saving: true, error: '', success: '' }));
    try {
      const data = await readJson(await fetch(
        `${API}/api/super-admin/tenants/${encodeURIComponent(adminManager.tenant.domain)}/admins/${encodeURIComponent(passwordReset.admin.id)}/password`,
        { method: 'PUT', headers: tokenHeaders(true), body: JSON.stringify({ newPassword: passwordReset.newPassword }) }
      ));
      setPasswordReset(current => ({ ...current, newPassword: '', confirmPassword: '', saving: false, error: '', success: `Password changed for ${data.admin.email}.` }));
    } catch (error) {
      setPasswordReset(current => ({ ...current, saving: false, error: error.message, success: '' }));
    }
  };

  const filteredTenants = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return tenants;
    return tenants.filter(tenant => `${tenant.name} ${tenant.domain} ${tenant.db_name}`.toLowerCase().includes(query));
  }, [search, tenants]);

  if (!isAuthenticated) {
    return (
      <main className="super-login-shell">
        <section className="super-login-copy" aria-labelledby="super-login-title">
          <div className="super-login-copy-inner">
            <a href="https://orderkit.co.uk" className="super-login-logo" aria-label="Orderkit website"><img src="/Orderkit_BLK.svg" alt="Orderkit" /></a>
            <h1 id="super-login-title">Welcome back to <em>Orderkit</em></h1>
            <ul className="super-login-points">
              <li><span className="super-login-point-icon" aria-hidden="true"><Store /></span><p>Manage restaurant tenants and administrator access</p></li>
              <li><span className="super-login-point-icon" aria-hidden="true"><Settings2 /></span><p>Control platform modules from one secure console</p></li>
              <li><span className="super-login-point-icon" aria-hidden="true"><ShieldCheck /></span><p>Protected by your private key and Google Authenticator</p></li>
            </ul>
            <p className="super-login-private">Private Orderkit platform access</p>
          </div>
        </section>
        <section className="super-login-panel">
          <div className="super-login-art" aria-hidden="true"><div className="super-login-art-wrap">
            <svg viewBox="0 0 1031 1628" className="super-login-art-shape" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M806.378 30.213L1022.24 319.466L416.122 469.942L203.55 179.215L806.378 30.213Z" fill="#2DFFCC"/>
              <path d="M837.55 418.669L1030.25 744.222L418.751 772.267L229.465 445.758L837.55 418.669Z" fill="url(#consoleGradient0)"/>
              <path d="M788.536 749.166L939.963 1057.73L348.082 900.009L200.555 591.791L788.536 749.166Z" fill="url(#consoleGradient1)"/>
              <path d="M636.928 1035.1L753.339 1350.98L216.002 1032.54L103.91 718.324L636.928 1035.1Z" fill="url(#consoleGradient2)"/>
              <path d="M433.098 1271.74L512.916 1516.51L75.9211 1220.58L0.511719 978.084L433.098 1271.74Z" fill="url(#consoleGradient3)"/>
              <defs>
                <linearGradient id="consoleGradient0" x1="709.145" y1="49.4097" x2="-334.317" y2="1683.24" gradientUnits="userSpaceOnUse"><stop offset=".219572" stopColor="#2DFFCC"/><stop offset=".556066" stopColor="#58FF9B"/><stop offset=".891353" stopColor="#E993FF"/></linearGradient>
                <linearGradient id="consoleGradient1" x1="870.421" y1="479.89" x2="34.0496" y2="1346.53" gradientUnits="userSpaceOnUse"><stop offset=".219572" stopColor="#2DFFCC"/><stop offset=".556066" stopColor="#58FF9B"/><stop offset=".891353" stopColor="#E993FF"/></linearGradient>
                <linearGradient id="consoleGradient2" x1="287.512" y1="148.564" x2="-356.409" y2="990.87" gradientUnits="userSpaceOnUse"><stop offset=".219572" stopColor="#2DFFCC"/><stop offset=".556066" stopColor="#58FF9B"/><stop offset=".891353" stopColor="#E993FF"/></linearGradient>
                <linearGradient id="consoleGradient3" x1="629.145" y1="-902.59" x2="-882.961" y2="-75.5996" gradientUnits="userSpaceOnUse"><stop offset=".219572" stopColor="#2DFFCC"/><stop offset=".556066" stopColor="#58FF9B"/><stop offset=".891353" stopColor="#E993FF"/></linearGradient>
              </defs>
            </svg>
          </div></div>
          <div className="super-login-card">
            <header><span>Orderkit Console</span><h2>Sign in</h2><p>Enter your super-admin key and the current six-digit code from Google Authenticator.</p></header>
            {pageError && <div className="super-login-error" role="alert">{pageError}</div>}
            <form onSubmit={event => { event.preventDefault(); loadTenants(false); }} className="super-login-form">
              <label><span>Super-admin key</span><input type="password" value={apiKey} onChange={event => setApiKey(event.target.value.replace(/[^\x20-\x7E]/g, ''))} autoComplete="current-password" required /></label>
              <label><span>Authenticator code</span><input type="text" inputMode="numeric" pattern="[0-9]{6}" value={totpCode} onChange={event => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" placeholder="000000" required maxLength={6} /></label>
              <button type="submit" disabled={loading || totpCode.length !== 6}>{loading ? 'Signing in…' : 'Sign in securely'}</button>
            </form>
            <footer><p>Your authenticator code refreshes every 30 seconds.</p><a href="mailto:support@orderkit.co.uk">Need help?</a></footer>
          </div>
        </section>
      </main>
    );
  }

  const logout = () => {
    handleUnauthorised();
    setApiKey('');
    setTotpCode('');
    navigate('/');
  };

  if (adminManager) {
    const tenant = adminManager.tenant;
    return (
      <ConsoleShell onHome={closeTenantWorkspace} onLogout={logout}>
        <header className="super-console-topbar">
          <button className="super-console-back" onClick={closeTenantWorkspace}><ArrowLeft />Restaurants</button>
          <div className="super-console-top-actions">
            <a className="super-console-button secondary" href={`https://${tenant.domain}`} target="_blank" rel="noreferrer"><ExternalLink />Visit website</a>
            {tenantTab !== 'access' && <button className="super-console-button primary" onClick={saveTenantSettings} disabled={tenantSaving}><Save />{tenantSaving ? 'Saving…' : 'Save changes'}</button>}
          </div>
        </header>
        <section className="super-console-pagehead tenant-head">
          <div className="super-console-tenant-mark"><Store /></div>
          <div><span className="super-console-eyebrow">Tenant workspace</span><h1>{tenant.name}</h1><p>{tenant.domain}</p></div>
          <span className={`super-console-status${tenant.active ? ' active' : ''}`}>{tenant.active ? 'Active' : 'Inactive'}</span>
        </section>

        <div className="super-console-tenant-layout">
          <nav className="super-console-tabs" aria-label="Tenant settings">
            {tabs.map(tab => { const Icon = tab.icon; return <button key={tab.id} className={tenantTab === tab.id ? 'is-active' : ''} onClick={() => { setTenantTab(tab.id); setTenantMessage(''); }}><Icon />{tab.label}<ChevronRight /></button>; })}
          </nav>
          <section className="super-console-workspace">
            {adminManager.loading && <div className="super-console-empty">Loading tenant…</div>}
            {adminManager.error && <div className="super-console-alert error">{adminManager.error}</div>}
            {tenantMessage && <div className={`super-console-alert${tenantMessage === 'Changes saved' || tenantMessage.startsWith('Special hours added') || tenantMessage.startsWith('Special hours removed') ? ' success' : ' error'}`}>{(tenantMessage === 'Changes saved' || tenantMessage.startsWith('Special hours')) && <Check />}{tenantMessage}</div>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'general' && <>
              <div className="super-console-section-head"><div><h2>General</h2><p>Restaurant identity and customer-facing contact information.</p></div></div>
              <div className="super-console-form-grid">
                <Field label="Tenant name"><input value={tenantForm.tenantName} onChange={e => updateField('tenantName', e.target.value)} /></Field>
                <Field label="Restaurant name"><input value={tenantForm.name} onChange={e => updateField('name', e.target.value)} /></Field>
                <Field label="Domain" hint="Domain changes require DNS and deployment work."><input value={tenant.domain} disabled /></Field>
                <Field label="Database" hint="The database identifier cannot be changed here."><div className="super-console-input-icon"><Database /><input value={tenant.db_name || ''} disabled /></div></Field>
                <Field label="Phone"><input value={tenantForm.phone} onChange={e => updateField('phone', e.target.value)} /></Field>
                <Field label="Instagram"><input value={tenantForm.instagram} onChange={e => updateField('instagram', e.target.value)} /></Field>
                <Field label="Address" wide><input value={tenantForm.address} onChange={e => updateField('address', e.target.value)} /></Field>
                <Field label="Postcode"><input value={tenantForm.postcode} onChange={e => updateField('postcode', e.target.value)} /></Field>
                <Field label="Description" wide><textarea rows="4" value={tenantForm.description} onChange={e => updateField('description', e.target.value)} /></Field>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'ordering' && <>
              <div className="super-console-section-head"><div><h2>Ordering</h2><p>Service times, charges and enabled ordering modules.</p></div></div>
              <div className="super-console-settings-card">
                <Toggle checked={tenantForm.modulePOS} onChange={value => saveOperationalToggle('modulePOS', value)} disabled={Boolean(operationalToggleSaving)} label="Point of sale" description="Allow this tenant to access the Orderkit tablet POS. Changes apply immediately." />
                <Toggle checked={tenantForm.pauseIncomingOrders} onChange={value => saveOperationalToggle('pauseIncomingOrders', value)} disabled={Boolean(operationalToggleSaving)} label="Pause incoming orders" description="Temporarily prevent customers from placing online orders. Changes apply immediately." />
              </div>
              <div className="super-console-form-grid">
                <Field label="Pickup time"><input value={tenantForm.pickupTime} onChange={e => updateField('pickupTime', e.target.value)} placeholder="15 - 20 mins" /></Field>
                <Field label="Delivery time"><input value={tenantForm.deliveryTime} onChange={e => updateField('deliveryTime', e.target.value)} placeholder="30 - 45 mins" /></Field>
                <Field label="Delivery fee (£)"><input type="number" min="0" step="0.01" value={tenantForm.deliveryFee} onChange={e => updateField('deliveryFee', e.target.value)} /></Field>
                <Field label="Minimum order (£)"><input type="number" min="0" step="0.01" value={tenantForm.minOrder} onChange={e => updateField('minOrder', e.target.value)} /></Field>
                <Field label="Service charge (£)"><input type="number" min="0" step="0.01" value={tenantForm.serviceCharge} onChange={e => updateField('serviceCharge', e.target.value)} /></Field>
                <Field label="Ordering interval"><select value={tenantForm.scheduleInterval} onChange={e => updateField('scheduleInterval', e.target.value)}><option value="15">15 minutes</option><option value="30">30 minutes</option><option value="60">60 minutes</option></select></Field>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'hours' && <>
              <div className="super-console-section-head"><div><h2>Opening hours</h2><p>Manage the permanent weekly schedule and date-specific closures or extended hours.</p></div></div>
              <div className="super-console-hours-card">
                {DAYS.map(day => {
                  const hours = tenantForm.openingHours[day] || DEFAULT_HOURS[day];
                  return <div className="super-console-hours-row" key={day}>
                    <label className="super-console-day-toggle"><input type="checkbox" checked={!hours.closed} onChange={event => updateHours(day, 'closed', !event.target.checked)} /><span>{day}</span></label>
                    {hours.closed ? <strong className="super-console-closed">Closed</strong> : <div className="super-console-time-range"><input type="time" value={hours.open || '11:30'} onChange={event => updateHours(day, 'open', event.target.value)} /><span>to</span><input type="time" value={hours.close || '22:30'} onChange={event => updateHours(day, 'close', event.target.value)} /></div>}
                  </div>;
                })}
              </div>

              <div className="super-console-subsection">
                <div className="super-console-section-head"><div><h3>Special hours and holidays</h3><p>Override the weekly schedule for a single day or date range.</p></div></div>
                <div className="super-console-form-grid super-console-special-draft">
                  <Field label="From"><input type="date" value={specialDraft.startDate} onChange={event => setSpecialDraft(current => ({ ...current, startDate: event.target.value, endDate: event.target.value > current.endDate ? event.target.value : current.endDate }))} /></Field>
                  <Field label="To"><input type="date" min={specialDraft.startDate} value={specialDraft.endDate} onChange={event => setSpecialDraft(current => ({ ...current, endDate: event.target.value }))} /></Field>
                  <Field label="Customer message" wide><input maxLength="120" value={specialDraft.label} onChange={event => setSpecialDraft(current => ({ ...current, label: event.target.value }))} placeholder="For example: Closed for Christmas" /></Field>
                  <label className="super-console-checkbox"><input type="checkbox" checked={specialDraft.closed} onChange={event => setSpecialDraft(current => ({ ...current, closed: event.target.checked }))} />Closed all day</label>
                  {!specialDraft.closed && <div className="super-console-time-range"><input type="time" value={specialDraft.open} onChange={event => setSpecialDraft(current => ({ ...current, open: event.target.value }))} /><span>to</span><input type="time" value={specialDraft.close} onChange={event => setSpecialDraft(current => ({ ...current, close: event.target.value }))} /></div>}
                </div>
                <div className="super-console-form-actions"><button type="button" className="super-console-button primary" onClick={addSpecialHours}><Plus />Add special hours</button></div>
                <div className="super-console-special-list">
                  {tenantForm.specialHours.length === 0 ? <p>No special hours or holiday closures have been added.</p> : tenantForm.specialHours.map(entry => <div key={entry.id} className="super-console-special-row"><div><strong>{entry.startDate === entry.endDate ? entry.startDate : `${entry.startDate} to ${entry.endDate}`}</strong><span>{entry.closed ? 'Closed all day' : `${entry.open}–${entry.close}`}{entry.label ? ` · ${entry.label}` : ''}</span></div><button type="button" onClick={() => removeSpecialHours(entry.id)} aria-label="Remove special hours"><Trash2 /></button></div>)}
                </div>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'appearance' && <>
              <div className="super-console-section-head"><div><h2>Brand & content</h2><p>Control customer-facing colours, imagery, promotional content and search metadata.</p></div></div>
              <div className="super-console-settings-block">
                <h3>Brand identity</h3>
                <div className="super-console-form-grid">
                  <Field label="Primary colour"><div className="super-console-colour"><input type="color" value={tenantForm.primaryColor} onChange={event => updateField('primaryColor', event.target.value)} /><input value={tenantForm.primaryColor} onChange={event => updateField('primaryColor', event.target.value)} /></div></Field>
                  <Field label="Secondary colour"><div className="super-console-colour"><input type="color" value={tenantForm.secondaryColor} onChange={event => updateField('secondaryColor', event.target.value)} /><input value={tenantForm.secondaryColor} onChange={event => updateField('secondaryColor', event.target.value)} /></div></Field>
                  <Field label="Logo URL" wide><div className="super-console-input-icon"><ImageIcon /><input type="url" value={tenantForm.logoUrl} onChange={event => updateField('logoUrl', event.target.value)} placeholder="https://example.com/logo.png" /></div></Field>
                  <Field label="Default menu item image" wide><input type="url" value={tenantForm.defaultItemImage} onChange={event => updateField('defaultItemImage', event.target.value)} placeholder="Fallback image URL" /></Field>
                </div>
              </div>
              <div className="super-console-settings-block">
                <h3>Homepage banner</h3>
                <div className="super-console-form-grid">
                  <Field label="Banner image URL" wide><input type="url" value={tenantForm.banner} onChange={event => updateField('banner', event.target.value)} /></Field>
                  <Field label="Banner text" wide><input value={tenantForm.bannerText} onChange={event => updateField('bannerText', event.target.value)} /></Field>
                </div>
              </div>
              <div className="super-console-settings-block">
                <h3>SEO and sharing</h3>
                <div className="super-console-form-grid">
                  <Field label="SEO title" wide><input value={tenantForm.seoTitle} onChange={event => updateField('seoTitle', event.target.value)} /></Field>
                  <Field label="Meta description" wide><textarea rows="3" value={tenantForm.metaDescription} onChange={event => updateField('metaDescription', event.target.value)} /></Field>
                  <Field label="Open Graph title" wide><input value={tenantForm.ogTitle} onChange={event => updateField('ogTitle', event.target.value)} /></Field>
                  <Field label="Open Graph image URL" wide><input type="url" value={tenantForm.ogImage} onChange={event => updateField('ogImage', event.target.value)} /></Field>
                  <Field label="Favicon URL" wide><input type="url" value={tenantForm.faviconUrl} onChange={event => updateField('faviconUrl', event.target.value)} /></Field>
                  <Field label="Public domain" hint="Used in tenant-generated emails and exports." wide><input value={tenantForm.publicDomain} onChange={event => updateField('publicDomain', event.target.value)} /></Field>
                </div>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'payments' && <>
              <div className="super-console-section-head"><div><h2>Payments</h2><p>Stripe credentials used by this tenant’s checkout and webhook.</p></div></div>
              <div className="super-console-security-note"><ShieldCheck /><div><strong>Credentials are write-only</strong><p>Saved secret values are never sent back to this browser. Leave a field empty to keep its existing value.</p></div></div>
              <div className="super-console-form-grid">
                <Field label="Stripe publishable key" wide><input value={tenantForm.stripePublishableKey} onChange={e => updateField('stripePublishableKey', e.target.value)} placeholder="pk_live_…" autoComplete="off" /></Field>
                <Field label="Stripe secret key" hint={tenantForm.stripeSecretKeyConfigured ? 'Configured — enter a new key only to replace it.' : 'Not configured.'} wide><input type="password" value={tenantForm.stripeSecretKey} onChange={e => updateField('stripeSecretKey', e.target.value)} placeholder={tenantForm.stripeSecretKeyConfigured ? 'Configured' : 'sk_live_…'} autoComplete="new-password" /></Field>
                <Field label="Webhook signing secret" hint={tenantForm.stripeWebhookSecretConfigured ? 'Configured — enter a new secret only to replace it.' : 'Not configured.'} wide><input type="password" value={tenantForm.stripeWebhookSecret} onChange={e => updateField('stripeWebhookSecret', e.target.value)} placeholder={tenantForm.stripeWebhookSecretConfigured ? 'Configured' : 'whsec_…'} autoComplete="new-password" /></Field>
                <Field label="Webhook endpoint" wide><div className="super-console-copy-value">https://{tenant.domain}/api/payments/webhook</div></Field>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'email' && <>
              <div className="super-console-section-head"><div><h2>Email</h2><p>SMTP service used for tenant-generated emails.</p></div></div>
              <div className="super-console-settings-card"><Toggle checked={tenantForm.smtpEnabled} onChange={value => updateField('smtpEnabled', value)} label="Enable SMTP email" description="Send restaurant emails using these tenant credentials." /><Toggle checked={tenantForm.smtpUseSsl} onChange={value => updateField('smtpUseSsl', value)} label="Use SSL" description="Use an encrypted SMTP connection." /></div>
              <div className="super-console-form-grid">
                <Field label="SMTP host"><input value={tenantForm.smtpHost} onChange={e => updateField('smtpHost', e.target.value)} placeholder="smtp.example.com" /></Field>
                <Field label="SMTP port"><input type="number" min="1" max="65535" value={tenantForm.smtpPort} onChange={e => updateField('smtpPort', e.target.value)} /></Field>
                <Field label="SMTP username"><input value={tenantForm.smtpUser} onChange={e => updateField('smtpUser', e.target.value)} autoComplete="off" /></Field>
                <Field label="From email"><input type="email" value={tenantForm.smtpFromEmail} onChange={e => updateField('smtpFromEmail', e.target.value)} /></Field>
                <Field label="SMTP password" hint={tenantForm.smtpPasswordConfigured ? 'Configured — enter a new password only to replace it.' : 'Not configured.'} wide><input type="password" value={tenantForm.smtpPassword} onChange={e => updateField('smtpPassword', e.target.value)} placeholder={tenantForm.smtpPasswordConfigured ? 'Configured' : 'Enter SMTP password'} autoComplete="new-password" /></Field>
              </div>
            </>}

            {!adminManager.loading && !adminManager.error && tenantTab === 'access' && <>
              <div className="super-console-section-head"><div><h2>Admin access</h2><p>Accounts permitted to sign in to this restaurant’s `/admin` area.</p></div><button className="super-console-button primary" onClick={() => { setShowCreateAdmin(current => !current); setNewAdmin(emptyNewAdmin); }}><Plus />{showCreateAdmin ? 'Close form' : 'Create admin'}</button></div>
              {showCreateAdmin && <form className="super-console-inline-form" onSubmit={createTenantAdmin}>
                <h3>Create admin login</h3><div className="super-console-form-grid">
                  <Field label="Name"><input value={newAdmin.name} onChange={e => setNewAdmin(current => ({ ...current, name: e.target.value, error: '', success: '' }))} required /></Field>
                  <Field label="Email"><input type="email" value={newAdmin.email} onChange={e => setNewAdmin(current => ({ ...current, email: e.target.value, error: '', success: '' }))} required /></Field>
                  <Field label="Password"><input type="password" minLength="10" value={newAdmin.password} onChange={e => setNewAdmin(current => ({ ...current, password: e.target.value, error: '', success: '' }))} required /></Field>
                  <Field label="Confirm password"><input type="password" minLength="10" value={newAdmin.confirmPassword} onChange={e => setNewAdmin(current => ({ ...current, confirmPassword: e.target.value, error: '', success: '' }))} required /></Field>
                </div>{newAdmin.error && <div className="super-console-alert error">{newAdmin.error}</div>}{newAdmin.success && <div className="super-console-alert success">{newAdmin.success}</div>}<button className="super-console-button primary" disabled={newAdmin.saving}>{newAdmin.saving ? 'Creating…' : 'Create admin login'}</button>
              </form>}
              <div className="super-console-admin-list">
                {adminManager.admins.length === 0 && <div className="super-console-empty">No administrator accounts found.</div>}
                {adminManager.admins.map(admin => <div className="super-console-admin-row" key={admin.id}><div className="super-console-avatar">{(admin.name || admin.email || 'A').charAt(0).toUpperCase()}</div><div><strong>{admin.name || 'Administrator'}</strong><span>{admin.email}</span></div><button className="super-console-button secondary" onClick={() => setPasswordReset({ ...emptyReset, admin })}>Change password</button></div>)}
              </div>
              {passwordReset.admin && <form className="super-console-inline-form" onSubmit={resetAdminPassword}><h3>Change password</h3><p>{passwordReset.admin.email}</p><div className="super-console-form-grid"><Field label="New password"><input type="password" minLength="10" value={passwordReset.newPassword} onChange={e => setPasswordReset(current => ({ ...current, newPassword: e.target.value, error: '', success: '' }))} required /></Field><Field label="Confirm password"><input type="password" minLength="10" value={passwordReset.confirmPassword} onChange={e => setPasswordReset(current => ({ ...current, confirmPassword: e.target.value, error: '', success: '' }))} required /></Field></div>{passwordReset.error && <div className="super-console-alert error">{passwordReset.error}</div>}{passwordReset.success && <div className="super-console-alert success">{passwordReset.success}</div>}<div className="super-console-form-actions"><button type="button" className="super-console-button secondary" onClick={() => setPasswordReset(emptyReset)}>Cancel</button><button className="super-console-button primary" disabled={passwordReset.saving}>{passwordReset.saving ? 'Changing…' : 'Change password'}</button></div></form>}
            </>}
          </section>
        </div>
      </ConsoleShell>
    );
  }

  return (
    <ConsoleShell onHome={closeTenantWorkspace} onLogout={logout}>
      <header className="super-console-topbar"><div><span className="super-console-eyebrow">Orderkit Console</span><strong>Restaurants</strong></div><button className="super-console-button primary" onClick={() => setShowCreateForm(current => !current)}><Plus />{showCreateForm ? 'Close' : 'New restaurant'}</button></header>
      <section className="super-console-pagehead"><div><h1>Restaurants</h1><p>Manage tenant configuration, services, payments and administrator access.</p></div></section>
      {pageError && <div className="super-console-alert error">{pageError}</div>}
      {showCreateForm && <form className="super-console-create" onSubmit={createTenant}>
        <div className="super-console-section-head"><div><h2>Create restaurant</h2><p>Create the tenant database record and its first administrator.</p></div></div>
        <div className="super-console-form-grid">
          <Field label="Restaurant name"><input value={newTenant.name} onChange={e => setNewTenant({ ...newTenant, name: e.target.value })} required /></Field>
          <Field label="Domain"><input value={newTenant.domain} onChange={e => setNewTenant({ ...newTenant, domain: e.target.value.trim().toLowerCase() })} placeholder="restaurant.co.uk" required /></Field>
          <Field label="Database name"><input value={newTenant.db_name} onChange={e => setNewTenant({ ...newTenant, db_name: e.target.value })} required /></Field>
          <Field label="Administrator name"><input value={newTenant.admin_name} onChange={e => setNewTenant({ ...newTenant, admin_name: e.target.value })} required /></Field>
          <Field label="Administrator email"><input type="email" value={newTenant.admin_email} onChange={e => setNewTenant({ ...newTenant, admin_email: e.target.value })} required /></Field>
          <Field label="Administrator password"><input type="password" minLength="10" value={newTenant.admin_password} onChange={e => setNewTenant({ ...newTenant, admin_password: e.target.value })} required /></Field>
        </div><div className="super-console-form-actions"><button type="button" className="super-console-button secondary" onClick={() => setShowCreateForm(false)}>Cancel</button><button className="super-console-button primary" disabled={loading}>{loading ? 'Creating…' : 'Create restaurant'}</button></div>
      </form>}
      <section className="super-console-directory-tools">
        <div className="super-console-search"><Search /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search restaurants, domains or databases" /></div>
        <span>{filteredTenants.length} restaurant{filteredTenants.length === 1 ? '' : 's'}</span>
      </section>
      <section className="super-console-table" aria-label="Restaurants">
        <div className="super-console-table-head"><span>Restaurant</span><span>Database</span><span>Created</span><span>Status</span><span /></div>
        {filteredTenants.map(tenant => <div className="super-console-table-row" key={tenant.id} onClick={() => openTenantWorkspace(tenant)}>
          <div className="super-console-restaurant-cell"><div className="super-console-tenant-mark"><Store /></div><div><strong>{tenant.name}</strong><span>{tenant.domain}</span></div></div>
          <span>{tenant.db_name}</span><span>{tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('en-GB') : '—'}</span><span><i className={`super-console-status${tenant.active ? ' active' : ''}`}>{tenant.active ? 'Active' : 'Inactive'}</i></span>
          <div className="super-console-row-actions"><a href={`https://${tenant.domain}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} aria-label={`Visit ${tenant.name}`}><Globe2 /></a>{tenant.active && <button onClick={e => { e.stopPropagation(); deleteTenant(tenant); }}>Deactivate</button>}<ChevronRight /></div>
        </div>)}
        {!loading && filteredTenants.length === 0 && <div className="super-console-empty">No restaurants match your search.</div>}
      </section>
    </ConsoleShell>
  );
}
